import React, { useEffect, useState } from "react";
import NumberFormat from "react-number-format";

import * as Yup from "yup";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Popover,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { useFormik } from "formik";
import { useActionHook } from "hooks";
import { useReportsActions } from "store";
import { ReportCashflowPreset, ReportCashflowSetting, ReportPredictionTableRow } from "store/types";

import { DescriptionContent } from "./DescriptionContent";
import { EditRatesContent } from "./EditRatesContent";

interface Props {
  id?: number;
  presets: ReportCashflowPreset[];
  set_id?: number | null;
  settingType?: ReportPredictionTableRow["type"];
  onCancel: () => void;
  onSubmit: (values: CashflowSettingForm) => Promise<void>;
}

const customPreset: ReportCashflowPreset = {
  id: 0,
  name: "Custom",
  contribution_frequency: "annual",
  bow_factor: 0,
  growth_rate: 0,
  yield: 0,
  investment_period: 0,
  contribution_rates: []
};

export type CashflowSettingForm = Pick<
  ReportCashflowSetting,
  "contribution_frequency" | "bow_factor" | "growth_rate" | "investment_period" | "contribution_rates"
> & {
  preset: ReportCashflowPreset | null;
};

const defaultValues: CashflowSettingForm = {
  preset: null,
  contribution_frequency: "annual",
  bow_factor: 0,
  growth_rate: 0,
  // yield: 0,
  investment_period: 0,
  // specific_parameter_id: 0,
  contribution_rates: []
};

const validationSchema = Yup.object({
  bow_factor: Yup.number().min(0, "Must be between 0 and 4").max(4, "Must be between 0 and 4").required("Required"),
  investment_period: Yup.number()
    .min(0, "Must be between 0 and 20")
    .max(20, "Must be between 0 and 20")
    .required("Required")
});

export const SettingForm: React.FC<Props> = ({ onCancel, id, onSubmit, presets, set_id, settingType }) => {
  const [descriptionAnchorEl, setDescriptionAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [editRatesAnchorEl, setEditRatesAnchorEl] = useState<HTMLButtonElement | null>(null);

  const { getCashflowSettingsAction } = useReportsActions();

  const { action: submitAction, loading: submitLoading } = useActionHook(onSubmit);

  const { setFieldValue, handleSubmit, values, setValues, errors, touched, isValid, setFieldTouched } =
    useFormik<CashflowSettingForm>({
      initialValues: defaultValues,
      validationSchema,
      validateOnBlur: true,
      validateOnMount: true,
      onSubmit: submitAction
    });

  const { loading, action } = useActionHook(getCashflowSettingsAction, {
    onSuccess: (data) => {
      if (Array.isArray(data)) {
        // empty array means that no settings found for this set
        const firstPreset = presets[0];

        // use first preset as default values
        setValues({
          ...defaultValues,
          preset: firstPreset,
          contribution_frequency: firstPreset.contribution_frequency,
          bow_factor: firstPreset.bow_factor,
          growth_rate: firstPreset.growth_rate,
          investment_period: firstPreset.investment_period,
          contribution_rates: firstPreset.contribution_rates
        });
        return;
      }

      const isPreset = data.preset_id ? presets.find(({ id }) => id === data.preset_id) : null;

      const preset = isPreset || customPreset;

      setValues({
        preset: preset,
        contribution_frequency: data.contribution_frequency,
        bow_factor: data.bow_factor,
        growth_rate: data.growth_rate,
        investment_period: data.investment_period,
        contribution_rates: data.contribution_rates
      });
    }
  });

  useEffect(() => {
    if (!id || !set_id || !settingType) return;
    action({ set_id, [`${settingType}_id`]: id });
  }, [id, set_id, settingType]);

  const handlePresetChange = (preset: ReportCashflowPreset) => {
    setFieldValue("preset", preset);

    if (preset.id === customPreset.id) {
      return setValues({ ...values, preset });
    }

    if (!preset) return;
    setValues({
      ...defaultValues,
      preset: preset,
      contribution_frequency: preset.contribution_frequency,
      bow_factor: preset.bow_factor,
      growth_rate: preset.growth_rate,
      investment_period: preset.investment_period,
      contribution_rates: preset.contribution_rates
    });
  };

  const handleSubmitRates = (rates: string[]) => {
    setFieldValue("contribution_rates", rates);
    setEditRatesAnchorEl(null);
  };

  return (
    <>
      <Stack
        sx={{
          p: 5,
          width: 375,
          height: 514,
          gap: "20px",
          overflow: "hidden",
          form: {
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden"
          }
        }}
      >
        <Stack direction="row" justifyContent="flex-start" alignItems="center">
          <Typography>Specific Parameters</Typography>
          <IconButton onClick={(e) => setDescriptionAnchorEl(e.currentTarget)}>
            <InfoOutlinedIcon />
          </IconButton>
        </Stack>
        <AsyncContentMui isLoading={loading} spinnerProps={{ size: 75 }}>
          <form onSubmit={handleSubmit}>
            <Stack
              sx={{
                flex: 1,
                overflow: "auto",
                gap: "20px",
                pr: "4px",
                py: "10px"
              }}
            >
              <Autocomplete
                fullWidth
                options={[...presets, customPreset]}
                value={values.preset || undefined}
                disableClearable
                onChange={(_, option) => handlePresetChange(option as ReportCashflowPreset)}
                renderOption={(props, option) => <li {...props}>{option.name}</li>}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => <TextField label="Altius Presets" {...params} />}
              />
              <Autocomplete
                fullWidth
                options={["annual", "quarterly"]}
                value={values.contribution_frequency}
                disabled
                onChange={(_, option) => {
                  setFieldValue("contribution_frequency", option);
                }}
                renderInput={(params) => <TextField label="Contribution Frequency" {...params} />}
                renderOption={(props, option) => <li {...props}>{option.charAt(0).toUpperCase() + option.slice(1)}</li>}
                getOptionLabel={(option) => option.charAt(0).toUpperCase() + option.slice(1)}
              />
              <Stack direction="row" justifyContent="space-between" gap="10px" sx={{ width: "100%" }}>
                <NumberFormat
                  fullWidth
                  label="Bow Factor"
                  customInput={TextField}
                  allowNegative={false}
                  name="bow_factor"
                  disabled={Boolean(values.preset?.id)}
                  decimalScale={2}
                  value={values.bow_factor}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                    setFieldValue("bow_factor", Number(e.target.value));
                    setFieldTouched("bow_factor", true, false);
                  }}
                  error={Boolean(touched.bow_factor && errors.bow_factor)}
                  helperText={touched.bow_factor && errors.bow_factor}
                />
                <NumberFormat
                  fullWidth
                  label="Growth Rate"
                  placeholder="10"
                  customInput={TextField}
                  disabled={Boolean(values.preset?.id)}
                  slotProps={{
                    input: {
                      endAdornment: <InputAdornment position="end">%</InputAdornment>
                    }
                  }}
                  allowNegative={false}
                  isNumericString
                  decimalScale={2}
                  value={values.growth_rate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    setFieldValue("growth_rate", Number(e.target.value))
                  }
                />
                {/* <NumberFormat
                fullWidth
                label="Growth Rate"
                placeholder="10"
                customInput={TextField}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">%</InputAdornment>
                  }
                }}
                allowNegative={false}
                isNumericString
                decimalScale={2}
                value={values.yield}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  setFieldValue("yield", e.target.value)
                }
              /> */}
              </Stack>

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ borderBottom: "1px solid #DBE8F5", width: "100%" }}
              >
                <Typography sx={{ fontSize: "16px", fontWeight: 400 }}>Investment life</Typography>
              </Stack>
              <Stack sx={{ width: "100%" }} direction="row" justifyContent="space-between" gap="10px">
                <NumberFormat
                  fullWidth
                  label="Default Period (Years)"
                  customInput={TextField}
                  allowNegative={false}
                  decimalScale={2}
                  disabled={Boolean(values.preset?.id)}
                  value={values.investment_period}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                    setFieldValue("investment_period", Number(e.target.value));
                    setFieldTouched("investment_period", true, false);
                  }}
                  error={Boolean(touched.investment_period && errors.investment_period)}
                  helperText={touched.investment_period && errors.investment_period}
                />
                {/* <Autocomplete<MockOptions, false>
                fullWidth
                options={mockOptions}
                value={mockOptions.find((option) => option.id === values.specific_parameter_id) || null}
                renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: MockOptions) => (
                  <li {...props} key={option.id}>
                    {option.title}
                  </li>
                )}
                getOptionLabel={(option: MockOptions) => option.title}
                onChange={(_, option: MockOptions | null) => {
                  setFieldValue("specific_parameter_id", option?.id);
                }}
                renderInput={(params) => <TextField label="Specific Parameter" {...params} />}
              /> */}
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ borderBottom: "1px solid #DBE8F5", width: "100%" }}
              >
                <Typography sx={{ fontSize: "16px", fontWeight: 400 }}>Contribution rates</Typography>
              </Stack>

              <Stack sx={{ flexDirection: "row", gap: "10px", justifyContent: "space-between", width: "100%" }}>
                <Stack
                  sx={{
                    flexDirection: "row",
                    gap: "10px",
                    flexGrow: 1,
                    alignItems: "flex-start",
                    overflow: "auto",
                    height: "40px"
                  }}
                >
                  {values.contribution_rates.length ? (
                    values.contribution_rates.map((item, index) => (
                      <Chip
                        key={item + index}
                        sx={{
                          bgcolor: "#F1F2F6",
                          color: "#071F35",
                          fontSize: "12px",
                          height: "24px",
                          "& .MuiChip-label": {
                            padding: "0 10px"
                          }
                        }}
                        label={`${item}%`}
                      />
                    ))
                  ) : (
                    <Chip label="No value" />
                  )}
                </Stack>
                <IconButton
                  disabled={Boolean(values.preset?.id)}
                  sx={{ bgcolor: "#F1F2F6", height: "24px", width: "24px" }}
                  onClick={(e) => setEditRatesAnchorEl(e.currentTarget)}
                >
                  <EditOutlinedIcon sx={{ fontSize: "12px" }} />
                </IconButton>
              </Stack>
            </Stack>
            <Stack
              direction="row"
              justifyContent="flex-end"
              gap="10px"
              sx={{
                width: "100%",
                mt: "auto",
                pt: 2,
                borderTop: "1px solid #DBE8F5"
              }}
            >
              <Button variant="outlined" onClick={onCancel} disabled={submitLoading}>
                Cancel
              </Button>
              {submitLoading ? (
                <Stack sx={{ width: 60, alignItems: "center" }}>
                  <CircularProgress size={24} />
                </Stack>
              ) : (
                <Button variant="contained" type="submit" disabled={!isValid}>
                  Edit
                </Button>
              )}
            </Stack>
          </form>
        </AsyncContentMui>
      </Stack>
      <Popover
        anchorEl={descriptionAnchorEl}
        open={Boolean(descriptionAnchorEl)}
        onClose={() => setDescriptionAnchorEl(null)}
      >
        <DescriptionContent onClose={() => setDescriptionAnchorEl(null)} />
      </Popover>

      <Popover
        open={Boolean(editRatesAnchorEl)}
        anchorEl={editRatesAnchorEl}
        onClose={() => setEditRatesAnchorEl(null)}
      >
        <EditRatesContent
          rates={values.contribution_rates.map((rate) => rate.toString())}
          onCancel={() => setEditRatesAnchorEl(null)}
          onSubmit={handleSubmitRates}
        />
      </Popover>
    </>
  );
};
